import { useRef, useState } from 'react';
import { usePageExperienceContext } from '@/features/experiences/contexts/PageExperienceContext';
import type { PageExperience } from '@/features/experiences/types';
import { AddButton } from '@/features/planner/components/AddButton';
import { DatePicker } from '@/features/planner/components/DatePicker';
import { EventFilter } from '@/features/planner/components/EventFilter';
import { MagicButton } from '@/features/planner/components/MagicButton';
import { ShowHiddenEventsToggle } from '@/features/planner/components/ShowHiddenEventsToggle';
import { TimezoneLabel } from '@/features/planner/components/TimezoneLabel';
import {
  useKeyCategories,
  ViewKeyRow,
  ViewKeyToggle,
} from '@/features/planner/components/ViewKey';
import { ViewPickerDropdown } from '@/features/planner/components/ViewPickerDropdown';
import { useActiveView } from '@/features/planner/hooks/useActiveView';
import { useDateRange } from '@/features/planner/hooks/useDateRange';
import { usePlannerViewRange } from '@/features/planner/hooks/usePlannerViewRange';
import { useIsCompactPlatformBar } from '@/hooks/useIsBreakpoint';
import { ExperienceTypes } from '@/types/gql.generated';
import { Box, Button, Flex } from '@/ui';
import { useOnboardingAnchor } from '../PlannerOnboarding/anchorContext';
import { PlatformBar } from './PlatformBar';
import { PlatformBarInteractions } from './PlatformBarInteractions';
import { useActiveViewCategoryColors } from './useActiveViewCategoryColors';
import { usePlatformBarVisibility } from './usePlatformBarVisibility';

export const ExperiencePlatformBar = () => {
  const { experience: parentExperience } = usePageExperienceContext();

  if (parentExperience.type !== ExperienceTypes.Planner) {
    return null;
  }

  return <ExperiencePlatformBarInner />;
};

// Inner component so data hooks only run when the bar is actually shown.
const ExperiencePlatformBarInner = () => {
  const isCompact = useIsCompactPlatformBar();
  const { experience: parentExperience } = usePageExperienceContext();
  const addEventsAnchorRef = useOnboardingAnchor('addEvents');
  const viewPickerAnchorRef = useOnboardingAnchor('viewPicker');
  const sendToCalendarAnchorRef = useOnboardingAnchor('sendToCalendar');
  const { activeChildId } = useActiveView(parentExperience);
  const activeChild = parentExperience.children.find(
    (c) => c.id === activeChildId
  );
  const [isKeyOpen, setIsKeyOpen] = useState(false);
  // Closing the Key unmounts the row along with the Close button focus is on,
  // which would otherwise drop focus to the body and lose a keyboard user's place
  // in the bar. Hand it back to the control that opened it.
  const keyToggleRef = useRef<HTMLButtonElement>(null);
  const closeKey = () => {
    setIsKeyOpen(false);
    keyToggleRef.current?.focus();
  };

  // The bar renders above ChildContent, so the only child it can reach directly
  // is the parent's embedded entry — id/type/kind/title/layoutId and nothing
  // else. Resolving a range from that shape reads two fields it doesn't carry:
  //
  // - weekStartDay, so a Sunday-start week view fell back to the layout's Monday
  //   default. The child rewrote the URL to its Sunday week, the bar re-anchored
  //   that to the *previous* Monday, and the picker showed a week behind what was
  //   rendered — with Next writing a range the child bounced straight back, so it
  //   never advanced.
  // - eventListStartDate/EndDate, so the selectable periods came from the ±5-year
  //   fallback window instead of the view's own, leaving prev/next enabled past
  //   the ends of a bounded planner.
  //
  // ChildPlannerView publishes the loaded child for exactly this; fall back to
  // the embedded entry for the frame before it lands, and guard on id so a
  // just-switched view can't resolve against the outgoing child's fields.
  const { experience: loadedChild } = usePlannerViewRange();
  const activeViewExperience =
    loadedChild?.id === activeChildId
      ? loadedChild
      : (activeChild as PageExperience | undefined);

  const { range, goToToday, hasDateRange } = useDateRange(activeViewExperience);

  const { hasCategoryColors } = useActiveViewCategoryColors({
    activeChildId,
    activeChild,
  });

  // Resolved here rather than inside the row: the same answer decides whether the
  // Key has anything to say, and so whether to offer the control at all.
  const keyCategories = useKeyCategories({
    experience: parentExperience,
    range,
  });

  // Single source of truth for what each control shows under the various
  // takeover flows (customize / add-view / Magic / Send) and breakpoints.
  const visible = usePlatformBarVisibility({
    hasDateRange,
    hasCategoryColors,
    hasKeyCategories: keyCategories.length > 0,
  });

  const left = (
    <Flex align="center" gap={2} minW={0}>
      {visible.magicButton && <MagicButton size="ah" />}
      {visible.viewPicker && (
        <Box minW={0} ref={viewPickerAnchorRef}>
          <ViewPickerDropdown
            compact={isCompact}
            experience={parentExperience}
          />
        </Box>
      )}
      {activeViewExperience && (
        <DatePicker
          compact={isCompact}
          experience={activeViewExperience}
          value={range}
        />
      )}
      {/* Never disabled, even sitting on today's own period. Disabling it read as
          the product locking the user out of a control rather than as "you are
          already here", so it stays live and a click re-anchors to the same range —
          a no-op you can't tell apart from staying put. This is what the legacy
          Schedule bar and the collaborative calendars do. */}
      {visible.today && (
        <Button size="ah" variant="ahSecondary" onClick={goToToday}>
          Today
        </Button>
      )}
      {visible.timezone && <TimezoneLabel />}
    </Flex>
  );

  const right = (
    <Flex align="center" gap={2} minW={0}>
      <ShowHiddenEventsToggle experience={parentExperience} range={range} />
      {visible.eventFilter && <EventFilter experience={parentExperience} />}
      {visible.key && (
        <ViewKeyToggle
          isOpen={isKeyOpen}
          ref={keyToggleRef}
          onToggle={() => setIsKeyOpen((open) => !open)}
        />
      )}
      {visible.interactions && (
        <Box ref={sendToCalendarAnchorRef}>
          <PlatformBarInteractions />
        </Box>
      )}
      {visible.magicButton && (
        <Box ref={addEventsAnchorRef}>
          <AddButton />
        </Box>
      )}
    </Flex>
  );

  // Left open across view switches — the Key is a reading aid, and a view that
  // still has a legend shouldn't make the user re-open it. A view without one
  // drops the row (and its toggle) until it comes back.
  const below =
    visible.key && isKeyOpen ? (
      <ViewKeyRow
        categories={keyCategories}
        experience={parentExperience}
        onClose={closeKey}
      />
    ) : undefined;

  return <PlatformBar below={below} left={left} right={right} />;
};
